import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';import date from 'quasar/src/utils/date/date.js';;
import useSession from '@/hooks/session';
import useUtility from '../hooks/utility';

export default function useUiUtilities() {
  const $q = useQuasar();
  const { sessionDetails } = useSession();

  const { getBlobDocument } = useUtility();

  const addSpacingOnCaps = (words) => {
    let result = words;
    result = result.replace(/([A-Z])/g, ' $1').trim();
    result = result.charAt(0).toUpperCase() + result.slice(1);

    return result;
  };

  const createBlobUrl = (data, fileType) => {
    const blob = new Blob([data], { type: fileType });
    const blobUrl = URL.createObjectURL(blob);

    return blobUrl;
  };

  const createDownloadableFile = (data, fileName, fileType) => {
    const blobUrl = createBlobUrl(data, fileType);
    const element = document.createElement('a');
    element.setAttribute('href', blobUrl);
    element.setAttribute('download', fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(blobUrl);
  };

  // todo: remove category, and addPdfText check, only use name as pdf name
  const exportPdf = async (blobId, category, name = '', addPdfText = true) => {
    try {
      if (category === 'complexBlob') {
        category = 'Document'; // eslint-disable-line no-param-reassign
      }
      const result = await getBlobDocument(blobId);
      const categoryWithSpaces = addSpacingOnCaps(category);
      name = addPdfText ? `${name} ${categoryWithSpaces}.pdf` : name; // eslint-disable-line no-param-reassign

      createDownloadableFile(result, name, 'appliation/pdf');
    } catch (error) {
      window.alert(error);
    }
  };

  const notifyResult = (statusCode, response = null, sticky = false, successMessage = null) => {
    if (statusCode === 200 || statusCode === 204) {
      $q.notify({
        message: successMessage ?? 'Successfully Updated',
        color: 'info',
        position: 'top',
        textColor: 'white',
      });
    } else if (statusCode === 201) {
      $q.notify({
        message: successMessage ?? 'Successfully Created',
        color: 'info',
        position: 'top',
        textColor: 'white',
      });
    } else if (sticky) {
      $q.notify({
        message: response?.data?.message
          ? response?.data?.message
          : (response?.data?.title ?? response?.data ?? 'Something Went Wrong...'),
        color: 'negative',
        position: 'top',
        textColor: 'white',
        timeout: 0,
        actions: [
          { label: 'Dismiss', color: 'white', handler: () => { /* ... */ } },
        ],
      });
    } else {
      $q.notify({
        message: response?.data?.message
          ? response?.data?.message
          : (response?.data?.title ?? response?.data ?? 'Something Went Wrong...'),
        color: 'warning',
        position: 'top',
        textColor: 'white',
      });
    }
  };

  const hasPassedAppilcationDeadline = () => {
    const currentDateUpdated = new Date();
    const deadlineUpdated = new Date(sessionDetails?.value?.applicationDeadline.replace(/-/g, '/'));

    const result = currentDateUpdated > deadlineUpdated;
    return result;
  };

  const isDefaultDateTime = (dateTime) => {
    if (dateTime === '0001-01-01T00:00:00' || dateTime === '2001-01-01T00:00:00'
      || dateTime === '01/01/1' || dateTime === '01/01/2001') {
      return true;
    }

    return false;
  };

  const isDateAfterCurrentDate = (targetDate) => date.getDateDiff(
    new Date(),
    new Date(targetDate),
  ) > 0;

  const isDateBeforeCurrentDate = (targetDate) => date.getDateDiff(
    new Date(),
    new Date(targetDate),
  ) < 0;

  const countries = [
    'Andorra',
    'United Arab Emirates (the)',
    'Afghanistan',
    'Antigua and Barbuda',
    'Anguilla',
    'Albania',
    'Armenia',
    'Angola',
    'Antarctica',
    'Argentina',
    'American Samoa',
    'Austria',
    'Australia',
    'Aruba',
    'Åland Islands',
    'Azerbaijan',
    'Bosnia and Herzegovina',
    'Barbados',
    'Bangladesh',
    'Belgium',
    'Burkina Faso',
    'Bulgaria',
    'Bahrain',
    'Burundi',
    'Benin',
    'Saint Barthélemy',
    'Bermuda',
    'Brunei Darussalam',
    'Bolivia (Plurinational State)',
    'Bonaire, Sint Eustatius & Saba',
    'Brazil',
    'Bahamas (the)',
    'Bhutan',
    'Bouvet Island',
    'Botswana',
    'Belarus',
    'Belize',
    'Canada',
    'Cocos (Keeling) Islands (the)',
    'Congo (Democratic Republic)',
    'Central African Republic (the)',
    'Congo (the)',
    'Switzerland',
    "Côte d',Ivoire",
    'Cook Islands (the)',
    'Chile',
    'Cameroon',
    'China',
    'Colombia',
    'Costa Rica',
    'Cuba',
    'Cabo Verde',
    'Curaçao',
    'Christmas Island',
    'Cyprus',
    'Czechia',
    'Germany',
    'Djibouti',
    'Denmark',
    'Dominica',
    'Dominican Republic (the)',
    'Algeria',
    'Ecuador',
    'Estonia',
    'Egypt',
    'Western Sahara*',
    'Eritrea',
    'Spain',
    'Ethiopia',
    'Finland',
    'Fiji',
    'Falkland Islands [Malvinas]',
    'Federated States of Micronesia',
    'Faroe Islands (the)',
    'France',
    'Gabon',
    'United Kingdom',
    'Grenada',
    'Georgia',
    'French Guiana',
    'Guernsey',
    'Ghana',
    'Gibraltar',
    'Greenland',
    'Gambia (the)',
    'Guinea',
    'Guadeloupe',
    'Equatorial Guinea',
    'Greece',
    'South Georgia',
    'Guatemala',
    'Guam',
    'Guinea-Bissau',
    'Guyana',
    'Hong Kong',
    'Heard and McDonald Islands',
    'Honduras',
    'Croatia',
    'Haiti',
    'Hungary',
    'Indonesia',
    'Ireland',
    'Israel',
    'Isle of Man',
    'India',
    'British Indian Ocean Terr',
    'Iraq',
    'Iran (Islamic Republic of)',
    'Iceland',
    'Italy',
    'Jersey',
    'Jamaica',
    'Jordan',
    'Japan',
    'Kenya',
    'Kyrgyzstan',
    'Cambodia',
    'Kiribati',
    'Comoros (the)',
    'Saint Kitts and Nevis',
    'North Korea',
    'South Korea',
    'Kuwait',
    'Cayman Islands (the)',
    'Kazakhstan',
    'Lebanon',
    'Saint Lucia',
    'Liechtenstein',
    'Sri Lanka',
    'Liberia',
    'Lesotho',
    'Lithuania',
    'Luxembourg',
    'Latvia',
    'Libya',
    'Morocco',
    'Monaco',
    'Moldova (the Republic of)',
    'Montenegro',
    'Saint Martin (French part)',
    'Madagascar',
    'Marshall Islands (the)',
    'Macedonia',
    'Mali',
    'Myanmar',
    'Mongolia',
    'Macao',
    'Northern Mariana Islands (the)',
    'Martinique',
    'Mauritania',
    'Montserrat',
    'Malta',
    'Mauritius',
    'Maldives',
    'Malawi',
    'Mexico',
    'Malaysia',
    'Mozambique',
    'Namibia',
    'New Caledonia',
    'Niger (the)',
    'Norfolk Island',
    'Nigeria',
    'Nicaragua',
    'Netherlands (the)',
    'Norway',
    'Nepal',
    'Nauru',
    'Niue',
    'New Zealand',
    'Oman',
    'Panama',
    'Peru',
    'French Polynesia',
    'Papua New Guinea',
    'Philippines (the)',
    'Pakistan',
    'Poland',
    'Saint Pierre and Miquelon',
    'Pitcairn',
    'Puerto Rico',
    'Palestine, State of',
    'Portugal',
    'Palau',
    'Paraguay',
    'Qatar',
    'Réunion',
    'Romania',
    'Serbia',
    'Russian Federation (the)',
    'Rwanda',
    'Saudi Arabia',
    'Solomon Islands',
    'Seychelles',
    'Sudan (the)',
    'Sweden',
    'Singapore',
    'Slovenia',
    'Svalbard and Jan Mayen',
    'Slovakia',
    'Sierra Leone',
    'San Marino',
    'Senegal',
    'Somalia',
    'Suriname',
    'South Sudan',
    'Sao Tome and Principe',
    'El Salvador',
    'Sint Maarten (Dutch part)',
    'Syrian Arab Republic',
    'Swaziland',
    'Turks and Caicos Islands (the)',
    'Chad',
    'French Southern Territories',
    'Togo',
    'Thailand',
    'Tajikistan',
    'Tokelau',
    'Timor-Leste',
    'Turkmenistan',
    'Tunisia',
    'Tonga',
    'Turkey',
    'Trinidad and Tobago',
    'Tuvalu',
    'Taiwan (Province of China)',
    'Tanzania, United Republic of',
    'Ukraine',
    'Uganda',
    'U.S Minor Outlying Islands',
    'United States of America',
    'Uruguay',
    'Uzbekistan',
    'Holy See (the)',
    'Saint Vincent and Grenadines',
    'Venezuela',
    'Virgin Islands (British)',
    'Virgin Islands (U.S.)',
    'Viet Nam',
    'Vanuatu',
    'Wallis and Futuna',
    'Samoa',
    'Yemen',
    'Mayotte',
    'South Africa',
    'Zambia',
    'Zimbabwe',
  ];

  const statesOptions = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ];

  const formatApplicationManagementRequestStatus = (requestTypeName, includeNone) => (
    requestTypeName === 'None'
      ? includeNone
        ? 'None'
        : ''
      : `${requestTypeName} Submitted`);

  /**
   * Converts formatted boolean column strings values to boolean values. If the column is not a
   * recognized boolean column, it will return the original value.
   * @param {any} column string name of the column
   * @param {any} value column value
   * @returns
   */
  const convertFormattedBooleanColumnToBoolean = (column, value) => {
    let result = value;

    if (column === 'agreement' || column === 'handbookAgreement') {
      if (value === 'Pending') {
        result = false;
      } else if (value === 'Received') {
        result = true;
      }
    } else if (column === 'agreement'
      || column === 'cleanlinessHabits'
      || column === 'hasAppliedToEap'
      || column === 'hasPetAllergies'
      || column === 'isMorningPerson'
      || column === 'isNightPerson'
      || column === 'isReturningToHousing'
      || column === 'roommatesClean'
      || column === 'roommatesHaveEmotionalSupportAnimal'
      || column === 'roommatesHaveSameDegree'
      || column === 'roommatesShare'
      || column === 'roommatesSocial'
      || column === 'shareEmail'
      || column === 'sharingHabits'
      || column === 'socialHabits'
      || column === 'stayInSameUnit'
      || column === 'animalInPlacement') {
      if (value === 'No') {
        result = false;
      } else if (value === 'Yes') {
        result = true;
      }
    }

    return result;
  };

  /**
   * Formats a date string to MM/DD/YYYY format
   * @param {any} dateString date represented as a string
   * @returns
   */
  const formatDate = (dateString) => date.formatDate(dateString, 'MM/DD/YYYY');

  const isShadowbrookComplex = (complexCode) => ['SB', 'SBK'].indexOf(complexCode) > -1;

  const isSummitComplex = (complexCode) => ['SMT', 'SUM'].indexOf(complexCode) > -1;

  const formatPacificTime = (timestamp) => { // eslint-disable-line
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles', // Pacific Time (Redmond)
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  return {
    addSpacingOnCaps,
    countries,
    createBlobUrl,
    createDownloadableFile,
    exportPdf,
    hasPassedAppilcationDeadline,
    isDefaultDateTime,
    isDateBeforeCurrentDate,
    isDateAfterCurrentDate,
    notifyResult,
    statesOptions,
    formatApplicationManagementRequestStatus,
    convertFormattedBooleanColumnToBoolean,
    formatDate,
    isShadowbrookComplex,
    isSummitComplex,
    formatPacificTime,
  };
}
